import { create } from 'zustand';
import type { User, AuthTokens, ApiUser } from '@/types';
import { tokenStorage, apiClient } from '@/lib/api-client';
import { queryClient } from '@/app/provider';

/**
 * Map API user response (snake_case) to frontend User type (camelCase)
 */
function mapApiUserToUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    fullName: apiUser.full_name,
    organizationName: apiUser.organization_name,
    isActive: apiUser.is_active,
    createdAt: apiUser.created_at,
    role: 'user', // Default role, could be extended if API provides it
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens) => void;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
}

// Clear any stale tokens/storage on fresh page load
tokenStorage.clearTokens();
localStorage.removeItem('auth-storage');

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setTokens: (tokens) => {
    tokenStorage.setTokens(tokens.access, tokens.refresh);
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.post('/api/v1/auth/login/', {
        email,
        password,
      });

      const { access, refresh, user: apiUser } = response.data.data || response.data;

      tokenStorage.setTokens(access, refresh);

      // Map snake_case API response to camelCase User type
      const user: User = apiUser ? mapApiUserToUser(apiUser) : { id: 0, email, role: 'user' };

      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail ||
                     error.response?.data?.message ||
                     'Invalid email or password';
      set({
        isLoading: false,
        error: message,
      });
      return false;
    }
  },

  logout: async () => {
    // Blacklist the refresh token on the backend before clearing locally
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await apiClient.post('/api/v1/auth/logout/', { refresh: refreshToken });
      } catch {
        // Continue with logout even if API call fails
        // Token will expire naturally if blacklisting fails
      }
    }

    tokenStorage.clearTokens();
    queryClient.clear();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  clearError: () => set({ error: null }),
}));
